import { Pipe, PipeTransform } from '@angular/core';

/**
 * Minimal, dependency-free markdown for assistant replies.
 * Input is HTML-escaped first; the result is bound via [innerHTML] so Angular's sanitizer still runs.
 */
@Pipe({
  name: 'chatMarkdown'
})
export class ChatMarkdownPipe implements PipeTransform {
  transform(value: string | null | undefined): string {
    if (!value) return '';

    const codeBlocks: string[] = [];
    let text = escapeHtml(value).replace(/```[\w-]*\n?([\s\S]*?)(```|$)/g, (_, code) => {
      codeBlocks.push(`<pre><code>${code.replace(/\n$/, '')}</code></pre>`);
      return `\u0000${codeBlocks.length - 1}\u0000`;
    });

    const html: string[] = [];
    let list: 'ul' | 'ol' | null = null;
    let paragraph: string[] = [];

    const flushParagraph = () => {
      if (paragraph.length) html.push(`<p>${paragraph.join('<br>')}</p>`);
      paragraph = [];
    };
    const closeList = () => {
      if (list) html.push(`</${list}>`);
      list = null;
    };

    for (const line of text.split('\n')) {
      const bullet = line.match(/^\s*[-*•]\s+(.*)$/);
      const numbered = line.match(/^\s*\d+[.)]\s+(.*)$/);
      const heading = line.match(/^\s*#{1,6}\s+(.*)$/);
      const block = line.match(/^\u0000(\d+)\u0000$/);

      if (bullet || numbered) {
        flushParagraph();
        const type = bullet ? 'ul' : 'ol';
        if (list !== type) {
          closeList();
          html.push(`<${type}>`);
          list = type;
        }
        html.push(`<li>${inline((bullet || numbered)![1])}</li>`);
      } else if (heading) {
        flushParagraph();
        closeList();
        html.push(`<p class="heading">${inline(heading[1])}</p>`);
      } else if (block) {
        flushParagraph();
        closeList();
        html.push(codeBlocks[+block[1]]);
      } else if (!line.trim()) {
        flushParagraph();
        closeList();
      } else {
        closeList();
        paragraph.push(inline(line));
      }
    }
    flushParagraph();
    closeList();

    // Code blocks that shared a line with other text
    return html.join('').replace(/\u0000(\d+)\u0000/g, (_, i) => codeBlocks[+i]);
  }
}

function escapeHtml(value: string): string {
  return value
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function inline(text: string): string {
  return text
    .replace(/`([^`]+)`/g, '<code>$1</code>')
    .replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>')
    .replace(/(^|[^*])\*([^*\s][^*]*)\*/g, '$1<em>$2</em>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, '<a href="$2" target="_blank" rel="noopener noreferrer">$1</a>');
}
